import { Component, Inject } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogContent, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { DialogData } from "@core/interfaces/dialog-data";
import { PatientHealthEvent } from "@core/models/PatientHealthEvent";
import { CatalogsService } from "@core/http/catalogs.service";
import { HealthEvent } from "@core/models/HealthEvent";
import { PatientHealthEventService } from "@core/http/table-data-services/patientHealthEvent.service";
import { UppercaseNoAccentDirective } from "@shared/directives/uppercase-no-accent.directive";
import { GeneralFunctionsService } from "@core/service/generalFunctions.service";
import { PrescriptionService } from "@core/http/table-data-services/prescription.service";
import { IMedicamentoActivo } from "@core/interfaces/medicamento-activo";
import { HttpErrorResponse } from "@angular/common/http";
import Swal from "sweetalert2";
import { HttpMesageHandlerService } from "@core/service/http-mesage-handler";
import { NgIf } from "@angular/common";

import { TutorService } from '@core/service/tutor.service';
import { UserService } from '@core/http/table-data-services/User.service';
import { AuthManagementService } from '@core/service/auth-management.service';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { PatientPinDialogComponent } from '@shared/components/patient-pin-dialog/patient-pin-dialog.component';

function maxTodayValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const d = new Date(control.value);
    const valueDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return valueDate > todayDate ? { maxDate: true } : null;
  };
}

function descripcionValidaValidator(): ValidatorFn {
  const allowedRegex = /^(?=.*[A-Za-z0-9Ññ])[A-Za-z0-9Ññ .,_#\/\-()]+$/;

  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '').toString();

    if (!raw) {
      return null; // required lo maneja aparte
    }

    // No espacios al inicio o final
    if (raw !== raw.trim()) {
      return { edgeSpaces: true };
    }

    const value = raw.trim();

    // No vacío real
    if (!value) {
      return { requiredTrimmed: true };
    }

    // Debe contener al menos una letra o numero y solo chars permitidos
    if (!allowedRegex.test(value)) {
      return { pattern: true };
    }

    return null;
  };
}

type TutorPacienteVm = {
  idPaciente: string;
  idUsuario: string;
  nombreCompleto: string;
  imagenSrc: string | null;
  initials: string;
};

@Component({
  selector: 'app-patient-health-event-create-edit',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatDatepickerModule,
    MatSelectModule,
    MatOptionModule,
    MatDialogClose,
    UppercaseNoAccentDirective,
    NgIf
  ],
  templateUrl: './patient-health-event-create-edit.component.html',
  styleUrl: './patient-health-event-create-edit.component.scss'
})
export class PatientHealthEventCreateEditComponent {
  action: string;
  dialogTitle: string;
  healthEventForm: UntypedFormGroup;
  healthEvent: PatientHealthEvent;

  events: HealthEvent[] = [];
  medications: IMedicamentoActivo[] = [];
  adverso: boolean = false;

  showTutorSelect = false;
  tutorPacientes: TutorPacienteVm[] = [];
  selectedPacienteId: string | null = null;

  allEvents: HealthEvent[] = [];
  canShowReactionEvent = true;

  private ownPacienteId: string | null = null;

  maxDate: Date = new Date();
  private readonly imageCache = new Map<string, string>();

  isLoadingMedsActivos = false;
  hasHiddenMeds = false;
  showHiddenMeds = false;
  hiddenMedsPin: string | null = null;

  formControl = new UntypedFormControl('', [Validators.required]);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData<PatientHealthEvent>,
    public dialogRef: MatDialogRef<PatientHealthEventCreateEditComponent>,
    public healthEventService: PatientHealthEventService,
    private prescriptionService: PrescriptionService,
    private catalogService: CatalogsService,
    private gfs: GeneralFunctionsService,
    private fb: UntypedFormBuilder,
    private httpMsgHandler: HttpMesageHandlerService,

    private authManagement: AuthManagementService,
    private tutorService: TutorService,
    private userService: UserService,

    private dialog: MatDialog,
  ) {
    this.action = data.action;

    if (this.action === 'edit') {
      this.dialogTitle = 'EDITAR: ' + data.entity.nombreEvento;
      this.healthEvent = data.entity;
    } else {
      this.dialogTitle = 'NUEVO EVENTO DE SALUD';
      this.healthEvent = new PatientHealthEvent(data.entity);
    }

    this.healthEventForm = this.createContactForm();

    // ✅ Normalizar desde el inicio: si viene vacío, queda null
    this.ownPacienteId = this.normalizePacienteId(
      this.healthEventForm.get('idPaciente')?.value
    );

    this.catalogService.getEventosDeSalud().subscribe({
      next: (value) => {
        this.allEvents = value.data ?? [];
        this.applyEventsFilter();
        this.loadReactionAvailability();
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });

    this.tryLoadTutorPacientes();
  }

  private applyEventsFilter(): void {
    this.events = (this.allEvents ?? []).filter((evento: HealthEvent) => {
      const idEvento = Number((evento as any)?.idEvento ?? 0);

      // 14 = REACCION ADVERSA A MEDICAMENTO
      if (idEvento === 14) {
        return this.canShowReactionEvent;
      }

      return true;
    });

    const currentEvent = Number(this.healthEventForm.get('eventoDeSalud')?.value ?? 0);

    if (currentEvent === 14 && !this.canShowReactionEvent) {
      this.healthEventForm.patchValue({
        eventoDeSalud: null,
        medicamentoSeleccionado: ''
      });
      this.adverso = false;
      this.medications = [];
    }
  }

  private loadReactionAvailability(): void {
    const idPacienteToSend =
      this.showTutorSelect
        ? this.normalizePacienteId(this.selectedPacienteId)
        : null;

    this.prescriptionService.getActiveMedications(idPacienteToSend, null).subscribe({
      next: (resp: any) => {
        const data = resp?.data ?? resp ?? {};
        const hasHidden = Boolean(data?.hasHidden ?? data?.HasHidden ?? false);
        const meds = data?.medicamentos ?? data?.Medicamentos ?? [];

        this.canShowReactionEvent = hasHidden || (Array.isArray(meds) && meds.length > 0);
        this.applyEventsFilter();
      },
      error: () => {
        this.canShowReactionEvent = false;
        this.applyEventsFilter();
      },
    });
  }

  /**
   * ✅ Convierte '', '   ', undefined en null
   */
  private normalizePacienteId(value: unknown): string | null {
    const normalized = (value ?? '').toString().trim();
    return normalized ? normalized : null;
  }

  private tryLoadTutorPacientes(): void {
    const userData = this.authManagement.userData();
    const idTutor = (userData?.IdUsuario ?? userData?.idUsuario ?? '').toString().trim();

    if (!idTutor) {
      this.showTutorSelect = false;
      return;
    }

    this.tutorService.getPacientesByTutor(idTutor).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res ?? [];
        const list = Array.isArray(payload) ? payload : [];

        if (!list.length) {
          this.showTutorSelect = false;
          this.tutorPacientes = [];
          this.selectedPacienteId = null;
          return;
        }

        this.showTutorSelect = true;

        this.tutorPacientes = list
          .map((row: any) => {
            const idPaciente = String(row?.idPaciente ?? row?.IdPaciente ?? '').trim();
            const idUsuario = String(row?.idUsuario ?? row?.IdUsuario ?? '').trim();

            const n = (row?.nombres ?? row?.Nombres ?? '').toString();
            const a1 = (row?.primerApellido ?? row?.PrimerApellido ?? '').toString();
            const a2 = (row?.segundoApellido ?? row?.SegundoApellido ?? '').toString();

            const nombreCompleto = `${n} ${a1} ${a2}`.replace(/\s+/g, ' ').trim();
            const initials = this.getInitials(nombreCompleto);

            return {
              idPaciente,
              idUsuario,
              nombreCompleto: nombreCompleto || 'PACIENTE',
              imagenSrc: null,
              initials,
            } as TutorPacienteVm;
          })
          .filter((x: TutorPacienteVm) => !!x.idPaciente);

        const currentIdPaciente = this.normalizePacienteId(
          this.healthEventForm.get('idPaciente')?.value
        );

        const exists = this.tutorPacientes.some(p => p.idPaciente === currentIdPaciente);
        this.selectedPacienteId = exists ? currentIdPaciente : null;

        this.tutorPacientes.forEach((p) => this.loadUserImage(p.idUsuario, p.idPaciente));

        if (this.healthEventForm.get('eventoDeSalud')?.value === 14) {
          this.resetHiddenMedsState();
          this.fetchMedications();
        }
      },
      error: () => {
        this.showTutorSelect = false;
        this.tutorPacientes = [];
        this.selectedPacienteId = null;
      },
    });
  }

  onTutorPacienteChange(): void {
    const idPaciente = this.normalizePacienteId(this.selectedPacienteId);

    if (!idPaciente) {
      this.healthEventForm.get('idPaciente')?.setValue(null);
    } else {
      this.healthEventForm.get('idPaciente')?.setValue(idPaciente);
    }

    this.loadReactionAvailability();

    if (this.healthEventForm.get('eventoDeSalud')?.value === 14) {
      this.showHiddenMeds = false;
      this.hiddenMedsPin = null;
      this.fetchMedications();
    }
  }

  onEventChange(eventId: number) {
    if (eventId === 14) {
      this.adverso = true;
      this.resetHiddenMedsState();
      this.fetchMedications();
    } else {
      this.adverso = false;
      this.medications = [];
      this.resetHiddenMedsState();
    }
  }

  fetchMedications(): void {
    const idPacienteToSend =
      this.showTutorSelect
        ? this.normalizePacienteId(this.selectedPacienteId)
        : null;

    const pinToSend: string | null = this.showHiddenMeds
      ? (this.hiddenMedsPin ?? null)
      : null;

    this.isLoadingMedsActivos = true;

    this.prescriptionService.getActiveMedications(idPacienteToSend, pinToSend).subscribe({
      next: (resp: any) => {
        const data = resp?.data ?? resp ?? {};

        this.hasHiddenMeds = Boolean(data?.hasHidden ?? data?.HasHidden ?? false);

        const meds = data?.medicamentos ?? data?.Medicamentos ?? [];
        this.medications = Array.isArray(meds) ? meds : [];

        this.isLoadingMedsActivos = false;
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingMedsActivos = false;
        this.hasHiddenMeds = false;
        this.medications = [];
        this.showHiddenMeds = false;
        this.hiddenMedsPin = null;
        this.httpMsgHandler.handleMsg(err);
      },
    });
  }

  async toggleMedicamentosOcultos(): Promise<void> {
    if (this.showHiddenMeds) {
      this.showHiddenMeds = false;
      this.hiddenMedsPin = null;
      this.fetchMedications();
      return;
    }

    const dialogRef = this.dialog.open(PatientPinDialogComponent, {
      disableClose: false,
      autoFocus: false,
      width: '560px',
      maxWidth: '92vw',
      panelClass: ['rmd-pin-dialog-panel'],
      data: {
        mode: 'PACIENTE',
        title: 'MEDICAMENTOS OCULTOS',
        message: 'EL PACIENTE PROTEGIÓ ALGUNOS MEDICAMENTOS CON UN PIN. INGRESA EL PIN PARA MOSTRARLOS.',
        optional: true,
      },
    });

    const pin = await firstValueFrom(dialogRef.afterClosed());

    if (pin === undefined as any) return;

    if (!pin) {
      this.showHiddenMeds = false;
      this.hiddenMedsPin = null;
      this.fetchMedications();
      return;
    }

    this.showHiddenMeds = true;
    this.hiddenMedsPin = pin;
    this.fetchMedications();
  }

  private resetHiddenMedsState(): void {
    this.hasHiddenMeds = false;
    this.showHiddenMeds = false;
    this.hiddenMedsPin = null;
    this.isLoadingMedsActivos = false;
  }

  get showPinRequiredHint(): boolean {
    return this.hasHiddenMeds && !this.showHiddenMeds && (this.medications?.length ?? 0) === 0;
  }

  private loadUserImage(idUsuario: string, idPaciente: string): void {
    if (!idUsuario) return;

    const cached = this.imageCache.get(idUsuario);
    if (cached !== undefined) {
      this.applyImageToVm(idPaciente, cached || null);
      return;
    }

    this.userService.getUserImageBase64(idUsuario).subscribe({
      next: (img: string) => {
        const normalized = (img ?? '').trim();
        const value = normalized ? normalized : '';
        this.imageCache.set(idUsuario, value);
        this.applyImageToVm(idPaciente, value || null);
      },
      error: () => {
        this.imageCache.set(idUsuario, '');
        this.applyImageToVm(idPaciente, null);
      },
    });
  }

  private applyImageToVm(idPaciente: string, img: string | null) {
    const idx = this.tutorPacientes.findIndex((x) => x.idPaciente === idPaciente);
    if (idx < 0) return;
    this.tutorPacientes[idx].imagenSrc = img || null;
  }

  private getInitials(name: string): string {
    const parts = (name ?? '').toUpperCase().split(' ').filter(Boolean);
    const a = parts[0]?.[0] ?? 'P';
    const b = parts[1]?.[0] ?? '';
    return (a + b).trim();
  }

  getErrorMessage() {
    return this.formControl.hasError('required') ? 'CAMPO REQUERIDO' : 'ERROR';
  }

  createContactForm(): UntypedFormGroup {
    return this.fb.group({
      idEventoSalud: [this.healthEvent.idEventoSalud],
      idPaciente: [this.normalizePacienteId(this.healthEvent.idPaciente)],
      fecha: [this.healthEvent.fecha, [Validators.required, maxTodayValidator()]],
      eventoDeSalud: [this.healthEvent.eventoDeSalud, [Validators.required]],
      descripcion: [
        this.healthEvent.descripcion,
        [
          Validators.required,
          descripcionValidaValidator()
        ]
      ],
      nombreEvento: [this.healthEvent.nombreEvento],
      medicamentoSeleccionado: [''],
    });
  }

  trimDescripcion(): void {
    const control = this.healthEventForm.get('descripcion');
    const raw = (control?.value ?? '').toString();
    const trimmed = raw.trim();

    if (raw !== trimmed) {
      control?.setValue(trimmed);
      control?.updateValueAndValidity();
    }
  }

  submit() {}
  onNoClick(): void { this.dialogRef.close(); }

  public confirmAdd(): void {
    this.trimDescripcion();

    if (this.healthEventForm.invalid) {
      this.healthEventForm.markAllAsTouched();
      return;
    }

    const eventForm = this.healthEventForm.getRawValue();

    eventForm.descripcion = (eventForm.descripcion ?? '').toString().trim();

    if (this.showTutorSelect) {
      eventForm.idPaciente = this.normalizePacienteId(this.selectedPacienteId);
    } else {
      eventForm.idPaciente = null;
    }

    if (this.adverso) {
      this.processReaction(eventForm);
    } else {
      delete eventForm.medicamentoSeleccionado;
      this.processEvent(eventForm);
    }
  }

  processEvent(eventForm: any) {
    const data: PatientHealthEvent = {
      ...eventForm,
      idPaciente: this.normalizePacienteId(eventForm.idPaciente)
    };

    data.fecha = this.gfs.formatDate(data.fecha);
    delete data.nombreEvento;

    this.action === 'edit'
      ? this.healthEventService.updatePatientHealthEvent(data)
      : this.healthEventService.addPatientHealthEvent(data);
  }

  public processReaction(eventForm: any) {
    const med: IMedicamentoActivo = eventForm.medicamentoSeleccionado;

    const reaccion = {
      descripcion: eventForm.descripcion,
      idDetalleReceta: med.idDetalleReceta,
      idReceta: med.idReceta,
      medicamentoId: med.drug,
      medicamentoType: med.drugType,
    };

    const cleanData = this.gfs.mayusAcentos(reaccion);

    this.prescriptionService.reaccionCrearActualizar(cleanData).subscribe({
      next: (res) => {
        const code = res.code || 'CODIGO NO DISPONIBLE';
        const message = res.message || 'DESCRIPCION DESCONOCIDA';

        Swal.fire({
          icon: 'success',
          title: `¡CORRECTO!`,
          text: message,
          footer: `CODIGO: (${code})`,
        }).then(() => this.dialogRef.close(1));
      },
      error: (error: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(error);
      },
    });
  }
}
